import {useCallback, useState} from "react"

import {traceDrawerClearParams} from "@agenta/observability/traceDrawer"
import {closeTraceDrawerAtom, isDrawerOpenAtom} from "@agenta/observability/traceDrawer"
import {EnhancedDrawer} from "@agenta/ui/drawer"
import {useAtomValue, useSetAtom} from "jotai"

import TraceDrawerContent, {type TraceDrawerLayout} from "./TraceDrawerContent"

interface TraceDrawerProps {
    /**
     * `split` (default): the span tree beside the span content, sized for a desktop drawer.
     * `stacked`: the tree above the content, both at the panel's width, for a phone-width host
     * (`web/mobile`). The split columns need at least 720px, so on a phone they scrolled sideways.
     */
    layout?: TraceDrawerLayout
}

const TraceDrawer = ({layout = "split"}: TraceDrawerProps) => {
    const open = useAtomValue(isDrawerOpenAtom)
    const closeDrawer = useSetAtom(closeTraceDrawerAtom)

    const initialWidth = 1200
    const [drawerWidth, setDrawerWidth] = useState(initialWidth)

    // Drop the params in the SAME action that closes the drawer, not 320ms later when the slide
    // finishes. `syncTraceStateFromUrl` reopens a closed drawer whenever `?trace=` is still on the
    // URL, so that gap was a window in which any url sync reopened what the user had just closed —
    // close, reopen, close … until the tab locked up. Clearing first leaves nothing to reopen from.
    const handleClose = useCallback(() => {
        traceDrawerClearParams()
        closeDrawer()
    }, [closeDrawer])

    // Backstop for the paths that close the drawer without going through `handleClose` (Escape,
    // outside click). Idempotent: clearing params that are already gone is a no-op push.
    const handleAfterOpenChange = useCallback((isOpen: boolean) => {
        if (!isOpen) traceDrawerClearParams()
    }, [])

    const toggleWidth = useCallback(() => {
        setDrawerWidth((width) => (width === initialWidth ? 1920 : initialWidth))
    }, [initialWidth])

    return (
        <EnhancedDrawer
            closable={false}
            title={null}
            open={open}
            onClose={handleClose}
            width={drawerWidth}
            afterOpenChange={handleAfterOpenChange}
            styles={{body: {padding: 0}}}
            destroyOnHidden
        >
            {open && (
                <TraceDrawerContent
                    onClose={handleClose}
                    onToggleWidth={toggleWidth}
                    isExpanded={drawerWidth !== initialWidth}
                    layout={layout}
                />
            )}
        </EnhancedDrawer>
    )
}

export default TraceDrawer
